import { FormField } from "@secure-caseworker/shared";
import React, { useState, useRef } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";

type UploadedFile = {
  id: string;
  name: string;
  size: number;
};

interface DynamicFormFieldProps {
  field: FormField;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (fieldId: string, value: any) => void;
  error?: string;
  onFileUpload?: (
    fieldId: string,
    file: File,
    onProgress: (percent: number) => void,
  ) => Promise<UploadedFile>;
  onFileRemove?: (fieldId: string, fileId: string) => void;
}

export const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  value,
  onChange,
  error,
  onFileUpload,
  onFileRemove,
}) => {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const newValue =
      field.field_type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
    onChange(field.id, newValue);
  };

  const baseInputClasses = `w-full border rounded-lg px-4 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
    error
      ? "border-red-500 dark:border-red-400"
      : "border-gray-300 dark:border-gray-600"
  }`;

  switch (field.field_type) {
    case "textarea":
      return (
        <div className="mb-4">
          <label
            htmlFor={field.id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            id={field.id}
            value={value || ""}
            onChange={handleChange}
            required={field.required}
            placeholder={field.placeholder}
            rows={4}
            className={baseInputClasses}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );

    case "select":
      return (
        <div className="mb-4">
          <label
            htmlFor={field.id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            id={field.id}
            value={value || ""}
            onChange={handleChange}
            required={field.required}
            className={baseInputClasses}
          >
            <option value="">{field.placeholder || "Select an option"}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );

    case "checkbox":
      return (
        <div className="mb-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              id={field.id}
              checked={value || false}
              onChange={handleChange}
              required={field.required}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </label>
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );

    case "radio":
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option} className="flex items-center space-x-3">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={handleChange}
                  required={field.required}
                  className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {option}
                </span>
              </label>
            ))}
          </div>
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );

    case "file": {
      const uploadedFiles = (value as UploadedFile[] | undefined) || [];

      const handleFileSelect = async (
        e: React.ChangeEvent<HTMLInputElement>,
      ) => {
        const file = e.target.files?.[0];
        if (!file || !onFileUpload) return;

        setUploadError(null);
        setUploadProgress(0);

        try {
          const uploaded = await onFileUpload(
            field.id,
            file,
            setUploadProgress,
          );
          onChange(field.id, [...uploadedFiles, uploaded]);
        } catch (err) {
          setUploadError(err instanceof Error ? err.message : "Upload failed");
        } finally {
          setUploadProgress(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      };

      const handleRemove = (fileId: string) => {
        onFileRemove?.(field.id, fileId);
        onChange(
          field.id,
          uploadedFiles.filter((f) => f.id !== fileId),
        );
      };

      const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      };

      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>

          {/* Uploaded files list */}
          {uploadedFiles.length > 0 && (
            <div className="mb-3 space-y-2">
              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(file.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              error
                ? "border-red-400 dark:border-red-500"
                : "border-gray-300 dark:border-gray-600 hover:border-brand-500"
            } ${uploadProgress !== null ? "pointer-events-none opacity-60" : ""}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              id={field.id}
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploadProgress !== null}
            />
            {uploadProgress !== null ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 text-brand-500 animate-spin" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Uploading... {Math.round(uploadProgress)}%
                </span>
                <div className="w-full max-w-xs bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-brand-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {field.placeholder || "Click or drag to upload a file"}
                </span>
              </div>
            )}
          </div>

          {uploadError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {uploadError}
            </p>
          )}
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );
    }

    case "text":
    case "email":
    case "number":
    case "tel":
    default:
      return (
        <div className="mb-4">
          <label
            htmlFor={field.id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={field.field_type}
            id={field.id}
            value={value || ""}
            onChange={handleChange}
            required={field.required}
            placeholder={field.placeholder}
            className={baseInputClasses}
          />
          {error && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
      );
  }
};
